/**
 * Dialog for promoting a canonical piece to a Wikidata reference.
 *
 * Allows entering a Wikidata work QID, fetches the title for confirmation,
 * then promotes the canonical piece (updating all linked suggestions).
 */

import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@brent/core-ui/ui'
import { Button } from '@brent/core-ui/ui'
import { Input } from '@brent/core-ui/ui'
import { Label } from '@brent/core-ui/ui'
import { Alert, AlertDescription } from '@brent/core-ui/ui'
import { Badge } from '@brent/core-ui/ui'
import { Loader2, ExternalLink, ArrowUpRight } from 'lucide-react'
import { getWorkById } from '@/services/api/wikidata'
import { usePromoteCanonicalPiece } from '@/hooks/useCanonicalPieces'
import { useToast } from '@/hooks/use-toast'
import type { CanonicalPiece } from '@/types/canonical-piece'

interface PromoteCanonicalPieceDialogProps {
  canonicalPiece: CanonicalPiece
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

const WIKIDATA_QID_PATTERN = /^Q\d+$/

export function PromoteCanonicalPieceDialog({
  canonicalPiece,
  open,
  onOpenChange,
  onSuccess,
}: PromoteCanonicalPieceDialogProps) {
  const [wikidataId, setWikidataId] = useState('')
  const { toast } = useToast()
  const promoteMutation = usePromoteCanonicalPiece()

  const isValidQid = WIKIDATA_QID_PATTERN.test(wikidataId.trim())

  const {
    data: wikidataWork,
    isLoading: isLoadingWork,
    error: workError,
  } = useQuery({
    queryKey: ['wikidata-work', wikidataId.trim()],
    queryFn: () => getWorkById(wikidataId.trim()),
    enabled: isValidQid,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setWikidataId('')
    }
  }, [open])

  const handlePromote = () => {
    if (!isValidQid || !wikidataWork) return

    promoteMutation.mutate(
      {
        pieceId: canonicalPiece.canonical_piece_id,
        input: {
          work_wikidata_id: wikidataId.trim(),
          work_title: wikidataWork.title,
        },
      },
      {
        onSuccess: (response) => {
          toast({
            title: 'Piece promoted',
            description: `Promoted to Wikidata reference ${wikidataId.trim()}. Updated ${response.promoted_suggestions} suggestion(s).`,
          })
          onOpenChange(false)
          onSuccess?.()
        },
        onError: (error: Error) => {
          toast({
            title: 'Error',
            description: error?.message || 'Failed to promote canonical piece',
            variant: 'destructive',
          })
        },
      },
    )
  }

  const handleClose = () => {
    if (!promoteMutation.isPending) {
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Promote to Wikidata</DialogTitle>
          <DialogDescription>
            Convert this canonical piece to a Wikidata reference. All linked suggestions will be
            updated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-1">
            <div className="text-sm font-medium">Current Piece</div>
            <div className="text-sm text-muted-foreground">
              {canonicalPiece.composer_name} —{' '}
              {canonicalPiece.work_title || '(Composer-only entry)'}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline">
                {canonicalPiece.suggestion_count} linked suggestion(s)
              </Badge>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="wikidata-work-id">Wikidata Work QID</Label>
            <Input
              id="wikidata-work-id"
              placeholder="e.g. Q1234"
              value={wikidataId}
              onChange={(e) => setWikidataId(e.target.value)}
              disabled={promoteMutation.isPending}
            />
            <p className="text-xs text-muted-foreground">
              Search Wikidata at{' '}
              <a
                href={`https://www.wikidata.org/w/index.php?search=${encodeURIComponent(canonicalPiece.work_title || '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                wikidata.org
              </a>
            </p>
          </div>

          {isLoadingWork && isValidQid && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Fetching work details from Wikidata...
            </div>
          )}

          {workError && isValidQid && (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to fetch work from Wikidata:{' '}
                {workError instanceof Error ? workError.message : 'Unknown error'}
              </AlertDescription>
            </Alert>
          )}

          {wikidataWork && (
            <Alert>
              <AlertDescription>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{wikidataWork.title}</span>
                    <a
                      href={`https://www.wikidata.org/wiki/${wikidataWork.wikidataId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                  {wikidataWork.catalogNumber && (
                    <div className="text-sm text-muted-foreground">
                      Catalog: {wikidataWork.catalogNumber}
                    </div>
                  )}
                  {wikidataWork.year != null && (
                    <div className="text-sm text-muted-foreground">Year: {wikidataWork.year}</div>
                  )}
                  <div className="text-sm mt-2">
                    This will update{' '}
                    <span className="font-medium">
                      {canonicalPiece.suggestion_count} linked suggestion(s)
                    </span>{' '}
                    to reference this Wikidata work.
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={promoteMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handlePromote}
            disabled={!isValidQid || !wikidataWork || promoteMutation.isPending || isLoadingWork}
          >
            {promoteMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Promoting...
              </>
            ) : (
              <>
                <ArrowUpRight className="mr-2 h-4 w-4" />
                Promote
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
